import { Box, Button, Drawer, Stack, Typography, useTheme } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { MouseEvent, useState } from 'react';
import {
  AuthorizedContent,
  DynamicForm,
  DynamicIcon,
  EllipsesMenu,
  QuickActionsButton,
  Tooltip,
} from 'src/components';
import { Logo } from 'src/components/Navbar/logo';
import {
  ListHeader,
  ListHeaderIcon,
  LogoContainer,
  PartnerHeaderContainer,
} from 'src/components/Navbar/navbar.styles';
import {
  CreateRecordFolderFormValues,
  useCreateFolderOptions,
} from 'src/components/Navbar/use-create-folder-options';
import {
  NavigationDataItem,
  NavigationGroup,
} from 'src/components/Navbar/use-navigation-items';
import { config } from 'src/core/config';
import { UserGroupRules } from 'src/graphql';
import { useScreenDimensions } from 'src/utils/system';
import { ActivePoliciesPreview } from './ActivePoliciesPreview/active-policies-preview';
import {
  getDrawerPaperSx,
  getDrawerRootSx,
  getMoreNavButtonSx,
  navPaneScrollSx,
  nonScrollingNavPaneSx,
  sidekickNavPaneSx,
} from './desktop-drawer.styles';
import { DrawerSection } from './drawer-section';
import { InboxPane } from './InboxPane/inbox-pane';
import { NavGroupTabs, NavigationPane } from './nav-group-tabs';
import { SearchPane } from './SearchPane/search-pane';
import { SidekickPane } from './SidekickPane/sidekick-pane';
import { SidekickTransitionBar } from './SidekickPane/sidekick-transition-bar';

export interface DesktopDrawerProps {
  title?: string;
  onAppMenuOpen: (event: MouseEvent<HTMLElement>) => void;
  onNavigateToItem: (item: NavigationDataItem) => void;
  navigationGroups: NavigationGroup[];
  settingsNavigationGroups: NavigationGroup[];
  activePane: NavigationPane;
  onPaneChange: (pane: NavigationPane) => void;
  toggleDrawer: () => void;
  isDrawerOpen: boolean;
  isPeeking?: boolean;
  navWidthPx?: number;
}

export const DesktopDrawer = ({
  title,
  onAppMenuOpen,
  onNavigateToItem,
  navigationGroups,
  settingsNavigationGroups,
  activePane,
  onPaneChange,
  toggleDrawer,
  isDrawerOpen,
  isPeeking,
  navWidthPx,
}: DesktopDrawerProps) => {
  const { isMobile } = useScreenDimensions();
  const theme = useTheme();
  // only one menu can be open at a time, so the open group's id is enough
  const [openAdornmentGroupId, setOpenAdornmentGroupId] = useState<string | null>(null);

  const {
    createFolderFormFields,
    hasStartedCreation,
    onSubmit: handleCreateFolderSubmit,
    submitButtonRef,
    editingFolderId,
    getCreateFolderOptions,
    handleEditFolder,
  } = useCreateFolderOptions();

  return (
    <Drawer
      open
      sx={getDrawerRootSx(theme, { isMobile, isDrawerOpen, isPeeking, navWidthPx })}
      variant="persistent"
      PaperProps={{
        sx: getDrawerPaperSx(theme, { isPeeking }),
      }}
    >
      <ListHeader>
        <PartnerHeaderContainer onClick={onAppMenuOpen}>
          <LogoContainer>
            <Logo color={config.envColor} />
          </LogoContainer>
          <Typography
            variant="subtitle2"
            noWrap
            overflow="hidden"
            textOverflow="ellipsis"
            data-testid="desktop-drawer-partner-name"
          >
            {title}
          </Typography>
          <DynamicIcon name="keyboard_arrow_down" />
        </PartnerHeaderContainer>
        <Stack direction="row" marginLeft="auto">
          <Tooltip title={isDrawerOpen ? 'Close' : 'Open'}>
            <ListHeaderIcon onClick={toggleDrawer}>
              {isDrawerOpen ? (
                <DynamicIcon name="left_panel_close" />
              ) : (
                <DynamicIcon name="left_panel_open" />
              )}
            </ListHeaderIcon>
          </Tooltip>
        </Stack>
      </ListHeader>

      <NavGroupTabs
        activePane={activePane}
        onPaneChange={onPaneChange}
        hasSettingsItems={settingsNavigationGroups.length > 0}
      />

      {/* key={activePane} so the child stagger re-runs on pane switch */}
      <Box
        key={activePane}
        sx={(() => {
          if (activePane === 'sidekick') return sidekickNavPaneSx;
          if (activePane === 'search' || activePane === 'inbox') return nonScrollingNavPaneSx;
          return navPaneScrollSx;
        })()}
      >
        {activePane === 'search' ? (
          <SearchPane
            onNavigateToItem={onNavigateToItem}
            onExpand={() => onPaneChange('home')}
          />
        ) : null}

        {activePane === 'inbox' ? <InboxPane /> : null}

        {activePane === 'sidekick' ? <SidekickPane /> : null}

        {activePane === 'home' ? (
          <AuthorizedContent allowedRules={[UserGroupRules.AccessToViewConversations]}>
            <Stack direction="row" alignItems="center" gap={1} mb={1.5} px={0.5}>
              <QuickActionsButton />
            </Stack>
          </AuthorizedContent>
        ) : null}

        {activePane === 'settings'
          ? settingsNavigationGroups.map((group) => (
              <Box key={group.id} mb={1}>
                <DrawerSection
                  groupId={group.id}
                  title={group.title}
                  navItems={group.items}
                  onClick={onNavigateToItem}
                />
              </Box>
            ))
          : null}

        {activePane === 'home' &&
          navigationGroups.map((group) => (
            <Box key={group.id}>
              <Box mb={1}>
                <Box sx={{ opacity: group.isHidden ? '0.7' : '1' }}>
                  <DrawerSection
                    icon={group.icon}
                    isFolder={!!group.folder}
                    groupId={group.id}
                    title={group.title}
                    navItems={group.items}
                    onClick={group.onClick || onNavigateToItem}
                    onReorder={group.onReorder}
                    isEditing={Boolean(group.folder && editingFolderId === group.folder.id)}
                    editTitle={
                      // TODO: it might make sense to move edit cb to group object
                      // will be considered later, leaving it to simplify a bunch of ui operations
                      group.folder
                        ? (name: string) => handleEditFolder(name, group.folder!)
                        : undefined
                    }
                    isAdornmentPinned={openAdornmentGroupId === group.id}
                    rightAdornment={
                      group.enableFolderCreation || group.folder ? (
                        <V2ThemeProvider>
                          <EllipsesMenu
                            icon="more-vertical"
                            iconLibrary="lucide"
                            options={getCreateFolderOptions(group.folder)}
                            sx={{ width: '1.5rem', height: '1.5rem' }}
                            onOpenChange={(open) =>
                              setOpenAdornmentGroupId(open ? group.id : null)
                            }
                          />
                        </V2ThemeProvider>
                      ) : null
                    }
                  />
                </Box>
              </Box>
              {hasStartedCreation &&
              navigationGroups.find((i) => i.enableFolderCreation)?.id === group.id ? (
                <DynamicForm<CreateRecordFolderFormValues>
                  style={{ gap: '1rem', marginBottom: '1rem' }}
                  fields={createFolderFormFields}
                  onSubmit={handleCreateFolderSubmit}
                >
                  <Button type="submit" ref={submitButtonRef} sx={{ display: 'none' }}>
                    Save
                  </Button>
                </DynamicForm>
              ) : null}
              {typeof group.shouldShowHiddenItems === 'boolean' && (
                <Box mb={1}>
                  <V2ThemeProvider>
                    <EllipsesMenu
                      showTitle
                      title="More"
                      icon="more-horizontal"
                      iconLibrary="lucide"
                      options={[
                        {
                          icon: group.shouldShowHiddenItems ? 'eye-off' : 'eye',
                          iconLibrary: 'lucide',
                          label: group.shouldShowHiddenItems
                            ? 'Always hide hidden'
                            : 'Always show hidden',
                          onClick: group.onShowHidden,
                        },
                        ...getCreateFolderOptions(),
                      ]}
                      sx={getMoreNavButtonSx(theme)}
                    />
                  </V2ThemeProvider>
                </Box>
              )}
            </Box>
          ))}
      </Box>

      {/* the preview is what Sidekick itself shows on its Policies subtab, so it
          would only duplicate the panel while that pane is open */}
      {activePane === 'sidekick' ? null : <ActivePoliciesPreview />}

      <SidekickTransitionBar />
    </Drawer>
  );
};
